/**
 * SHER-583. `GET /inpatients/discharge-diagnoses/...` — ICD-11 diagnoses from the discharge form,
 * for episodes that ended in the period. `departments` is ordered by how many of this diagnosis's
 * discharges each service contributed.
 */
export interface InpatientDischargeDiagnosis {
  code: string;
  name: string;
  discharges: number;
  /** Ordered by how many of this diagnosis's discharges each service contributed. */
  departments: string[];
  // SHER-583. Optional for the same reason as `DiagnosisStats.code`: the frontend ships to S3
  // independently of the API, so a build can be in front of an API that does not send these yet.
  // The Female/Male split is computed here rather than server-side because patient sex is
  // encrypted and the API cannot read it; these ids are all it can supply, and they are in the
  // same space as the outpatient table's `patientIds` so one lookup serves both.
  patientIds?: number[];
  /** Distinct patients, not episodes — a patient discharged twice with one code counts once. */
  patientsLength?: number;
}

export interface InpatientDischargeDiagnosesResponse {
  status: number;
  stats: {
    diagnoses: InpatientDischargeDiagnosis[];
    totalDischarges: number;
  };
}

/**
 * SHER-956. `GET /finances/category-billing-trends/...` — what `getBillingByServiceCategory`
 * writes (`sherwood-api` `analytics.controller.ts:4598-4650`), which is also what
 * `CategoryBillingTable` reads and what the API's own test asserts.
 *
 * The line range is cited rather than just the function name because a first draft of this type
 * said `{ total, count }`, copied from a DIFFERENT `categoryTotals` local in the same controller
 * (`analytics.controller.ts:4373`, inside `getBillingTrendsByServiceType`, emitted under
 * `byCategory` on another response). That is exactly the drift this ticket exists to stop, and
 * nothing caught it: the consumer takes `any`, so no compiler sees this declaration meet a value.
 */
export interface BillingCategoryTotals {
  totalBilled: number;
  totalPaid: number;
  totalPending: number;
  totalDiscount: number;
  totalExternalPaymentPaid: number;
  totalExternalPaymentPending: number;
  count: number;
}

export interface BillingByServiceCategoryStats {
  categoryTotals: Record<string, BillingCategoryTotals>;
}

export interface BillingByServiceCategoryResponse {
  status: number;
  stats: BillingByServiceCategoryStats;
  chartData: null;
  debug?: unknown;
}

/**
 * SHER-899. One insurer as the appointments-by-insurance endpoint now reports it. `code` is what
 * `ChartData.categories` holds on the same response, so the two readings can be joined.
 */
/**
 * What the API can say about who paid: SHER-898's seven categories, plus `NO_COVERAGE` for
 * appointments whose patient has no insurer at all — which is NOT `OTHER_OR_UNKNOWN`, since that
 * one means "an insurer nobody has categorised" and presupposes an insurer.
 *
 * Closed, and declared the way this module already declares such a set (`ANALYTICS_VIEW_MODE`),
 * because both tables are DOCUMENTED as legitimately empty on most tenants — so a config row
 * that misspells a category would render as the expected state and be invisible. A typo is a
 * compile error instead.
 */
export const PAYER_CATEGORY = {
  IPM: 'IPM',
  INSURANCE: 'INSURANCE',
  MUTUAL: 'MUTUAL',
  PRIVATE_PATIENT: 'PRIVATE_PATIENT',
  SPECIFIC_PROGRAM: 'SPECIFIC_PROGRAM',
  STAFF_OR_FREE: 'STAFF_OR_FREE',
  OTHER_OR_UNKNOWN: 'OTHER_OR_UNKNOWN',
  NO_COVERAGE: 'NO_COVERAGE',
} as const;

export type PayerCategory = typeof PAYER_CATEGORY[keyof typeof PAYER_CATEGORY];

export interface InsurerAppointments {
  code: string;
  name: string;
  payerCategory: PayerCategory;
  appointments: number;
}

export type ChartType =
  | 'bar'
  | 'line'
  | 'pie'
  | 'doughnut'
  | 'gauge'
  | 'demographic'
  | 'data-only'
  | 'data-value'
  | 'patient-bar'
  | 'productivity'
  | 'multi-chart'
  | 'donut'
  | 'bar-chart'
  | 'horizontal-bar'
  | 'appointment-stats'
  | 'appointment-outcomes'
  | 'distribuited'
  | 'appointment-type-table'
  | 'bar-stacked'
  | 'radar'
  | 'wait-time'
  | 'diagnosis-table'
  | 'discharge-diagnosis-table'
  | 'insurance-top-table'
  | 'category-billing-table'
  | 'sexDistribution'
  | 'patient-category-billing'
  | 'surgery-kpi-section'
  | 'surgery-procedure-table'
  | 'surgery-surgeon-table'
  | 'surgery-wait-distribution'
  | 'surgery-pipeline-status';

// SHER-562. The two view modes of the Outpatients tab. This is not only a UI state:
// every chart row in config/analyticsCharts.ts declares the tier it belongs to, and
// the hook fetches only the endpoints the active tier's visible charts need.
export const ANALYTICS_VIEW_MODE = {
  QUICK: 'quick',
  DETAILED: 'detailed',
} as const;

export type AnalyticsViewMode = typeof ANALYTICS_VIEW_MODE[keyof typeof ANALYTICS_VIEW_MODE];

export interface DepartmentStats {
  name: string;
  totalPatients: number;
}

export interface QueryItem {
  date: Date;
  amounts: number[];
}

export interface DepartmentData {
  departmentuuid: string;
  departmentname: string;
  patientcount: string;
}


export interface OutPatientResponse {
  status: number;
  trend: {
    type: "days" | "weeks" | "months" | "default";
    totals: number[];
    data: number[];
  };
  stats?: {
    [key: string]: DepartmentStats;
  };
  data?: DepartmentData[];
  query?: QueryItem[];
}

export interface DiagnosisStats {
  name: string;
  // SHER-306. Optional because it is newer than the clients that read this response: the
  // frontend ships to S3 independently of the API, so a build can be in front of an API that
  // does not send it yet. `name` falls back to the code server-side, so without this field a
  // reader cannot tell a resolved label from an unresolved one.
  code?: string;
  patientsLength: number;
  amount: number;
  patientIds: number[];
  // SHER-583. "Services principaux". Ordered busiest-first, never containing a blank. Optional
  // because it is newer than the clients that read this response.
  departments?: string[];
}

/**
 * What one chart source is worth to the tab: pending, failed, or drawable. Declared here rather
 * than in `useOutpatientsData` because `utils/demographics` produces one, and a util importing
 * a type back out of the hook that imports the util is a module cycle.
 */
export type SourceState =
  | { status: 'pending' }
  | { status: 'error'; error: Error }
  | { status: 'ok' };

/**
 * SHER-896. `GET /outpatients/unique-patients/...` — the outpatient population behind the
 * "Patients uniques" tile, mirroring `IUniqueOutpatientsResponse` in the API controller.
 *
 * `patients` holds `patientIdInvestigation` values, which is the id the client's redux
 * patients are keyed by. `firstAppointmentDates` maps each of them to the ISO calendar day of
 * their first appointment in the period, which is the date age is measured at.
 */
export interface UniquePatientsResponse {
  status: number;
  props: {
    patients: number[];
    firstAppointmentDates: Record<number, string>;
  };
}

export interface DiagnosisResponse {
  status: number;
  stats: {
    [diagnosisCode: string]: DiagnosisStats;
  };
  department: string;
  patients: number[];
}

// Types for clarity
export interface PatientInvestigation {
  idPatientInvestigation: number;
  department: {
      id: number;
      uuid: string;
      code: string;
      name: string;
      type: number;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
  };
}

interface PatientInvestigationStatsData {
  [date: string]: PatientInvestigation[];
}

export interface ActivityPatientsDistributionResponse {
  status: number;
  stats: PatientInvestigationStatsData;
}

export interface ChartSeries {
  name: string;
  data: number[];
}

export interface ChartData {
  series: ChartSeries[];
  categories: string[];
  count?: number;
}

/**
 * SHER-583. One ICD-11 diagnosis, as both diagnosis tables render it.
 *
 * This replaces three index-aligned arrays (`patientIdsByCategory`, `codesByCategory`,
 * `departmentsByCategory`) that used to hang off `ChartData` for one component's private use.
 * Each was documented as "parallel to `categories`" because nothing in the type could say so,
 * and SHER-880 is on record as a bug where exactly that alignment was broken silently. A row
 * cannot come apart from itself, so the invariant stops needing a comment.
 */
export interface DiagnosisTableRow {
  code: string;
  name: string;
  /**
   * Counted in the data layer from the decrypted patients, not by the table. Patient sex is
   * encrypted, so the API sends ids and the split can only be resolved in the browser — but it
   * is resolved once, where the rows are assembled, rather than on every render of every row.
   * Zero while the patient store is still decrypting; `patients` stays truthful throughout.
   */
  female: number;
  male: number;
  /** Ordered by how often each service saw this diagnosis. Never contains a blank. */
  departments: string[];
  /**
   * Distinct patients, from the API — never derived from the split, which can lag the decrypt.
   * `null` means the API did not say, which is NOT the same as none: against an endpoint that
   * predates this field every discharge row would otherwise read Total 0 beside a real, non-zero
   * Discharges count, indistinguishable from a diagnosis that genuinely had no patients.
   */
  patients: number | null;
  /** Episodes, inpatient only. A patient readmitted with one diagnosis is 1 patient, 2 discharges. */
  discharges?: number;
}

export interface DiagnosisTableData {
  rows: DiagnosisTableRow[];
}

/**
 * SHER-956. Everything ChartFactory can be handed.
 *
 * Named because it was an anonymous four-member union inline in `ChartFactoryProps`, and the
 * stats-shaped responses that also arrive there were simply missing from it — which is why every
 * `stats` read in that file was a type error. Read the block through `utils/statsResponse.ts`
 * rather than reaching into it: the members disagree about what they carry, which is the point.
 */
export type AnalyticsChartResponse =
  | OutPatientResponse
  | ChartData
  | DistributionResponse
  | IAppointmentTypeDataResponse
  | InpatientDischargeDiagnosesResponse
  | DiagnosisTableData
  | BillingByServiceCategoryResponse;

export interface ChartDataResponse {
  status: number;
  props: ChartData;
}

export interface DistributionResponse {
  status: number;
  chartData: ChartData;
  stats?: any
  trend?: any
  debug?: any
}

type StatPercentage = {
  [id: string]: {
    name: string;
    count: number;
    percentage: string;
  };
};

export interface AppointmentDepartmentStats {
  appointments: DepartmentStats[]
  departmentStats: StatPercentage
  totalOverduePercentage: string
  totalAppointments: number
  totalOverdue: number
}

export interface AppointmentDepartmentResponse {
  status: number;
  stats: AppointmentDepartmentStats;
}

export interface NewPatientStats {
  [id: string]: {
    name: string;
    newPatients: number;

  };
}

export interface DischargesWardsType {
  [key: string]: {
    name: string;
    discharges: number;
  };
}

export interface DischargesStats {
  [id: string]: {
    name: string;
    totalDischarges: number;
    wards: DischargesWardsType;
  }
}

export interface DischargesResponse {
  status: number;
  stats: DischargesStats;
}


export interface IAppointmentTypeData {
  appointmentType: string;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgDaysUntilAppointment: number;
  minDaysUntilAppointment: number;
  maxDaysUntilAppointment: number;
  mostCommonBookingMonth: string;
  mostCommonAppointmentMonth: string;
  mostCommonBookingDay: number;
  mostCommonAppointmentDay: number;
}

export interface IAppointmentTypeDataResponse {
  status: number;
  stats: IAppointmentTypeData[];
}

/** Query params mirrored from sherwood-api surgeryAnalyticsFilters (SHER-904). */
export interface SurgeryAnalyticsFiltersQuery {
  serviceInvestigationIds: number[];
  procedureServiceIds: number[];
  surgeonResearcherUuids: string[];
  statuses: number[];
}

export const EMPTY_SURGERY_ANALYTICS_FILTERS: SurgeryAnalyticsFiltersQuery = {
  serviceInvestigationIds: [],
  procedureServiceIds: [],
  surgeonResearcherUuids: [],
  statuses: [],
};

export interface SurgeryQuickKpiTotals {
  planned: number;
  completed: number;
  cancelled: number;
  currentlyWaitingPatients: number;
  medianReadyToOperationDays: number;
}

export interface SurgeryQuickKpisResponse {
  status: number;
  stats: { totals: SurgeryQuickKpiTotals };
}

export interface SurgeryProcedureRow {
  procedureKey: string;
  procedureLabel: string;
  episodeCount: number;
  completed: number;
  cancelled: number;
  medianReadyToOperationDays: number;
}

export interface SurgerySurgeonRow {
  surgeonUuid: string;
  surgeonLabel: string;
  episodeCount: number;
  completed: number;
  planned: number;
}

export interface SurgeryWaitBucket {
  label: string;
  minDays: number;
  maxDays: number | null;
  count: number;
}

export interface SurgeryPipelineRow {
  status: number;
  count: number;
}

export interface SurgeryChartData {
  series: { name: string; data: number[] }[];
  categories: string[];
  count: number;
  pipelineRows?: SurgeryPipelineRow[];
}

export interface SurgeryData {
  quickKpis: SurgeryQuickKpiTotals | null;
  periodActivity: SurgeryChartData;
  topProcedures: SurgeryChartData;
  byProcedure: SurgeryProcedureRow[];
  bySurgeon: SurgerySurgeonRow[];
  waitingTime: SurgeryChartData;
  pipelineByStatus: SurgeryChartData;
}